import { HttpService } from "core/services/http";

export class CVHttpService {
    static async loadCV(id) {
        const service = new HttpService();
        return await service.get(`/job/cv/${id}`, null);
    }

    static async loadCVByShareUUID(shareUUID) {
        const service = new HttpService();
        return await service.get(`/job/cv/share/${shareUUID}/`, null);
    }

    static async saveCV(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/${body.id}`, body);
        }
        return await service.post('/job/cv/', body);
    }

    static async deleteCV(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/${id}`);
    }

    static async loadCVList({ page = 1, size = 30 } = {}) {
        const service = new HttpService();
        return await service.get('/job/cv/', { page, size });
    }

    static async downloadPDF(cvId, style = 'style2') {
        const apiBase = HttpService.ApiBase;
        const token = HttpService.getToken();
        // If style is null, don't include template parameter - backend will auto-detect AI CVs
        const url = style 
            ? `${apiBase}/job/cv/${cvId}/generate-report/?template=${style}`
            : `${apiBase}/job/cv/${cvId}/generate-report/`;
        
        const response = await fetch(url, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            },
        });
        
        if (!response.ok) throw new Error('Failed to download PDF');
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `cv_${cvId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    static async downloadPDFByShareUUID(shareUUID, style = 'style2') {
        const apiBase = HttpService.ApiBase;
        const url = `${apiBase}/job/cv/share/${shareUUID}/generate-report/?template=${style}`;
        
        const response = await fetch(url);
        
        if (!response.ok) throw new Error('Failed to download PDF');
        
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `cv_${shareUUID}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    static getShareableURL(shareUUID) {
        return `${window.location.origin}/cv/share/${shareUUID}`;
    }

    // Section-specific endpoints
    static async saveJobExperience(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/jobexperience/${body.id}`, body);
        }
        return await service.post('/job/cv/jobexperience/', body);
    }

    static async deleteJobExperience(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/jobexperience/${id}`);
    }

    static async saveEducation(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/education/${body.id}`, body);
        }
        return await service.post('/job/cv/education/', body);
    }

    static async deleteEducation(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/education/${id}`);
    }

    static async saveUserSkill(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/userskill/${body.id}`, body);
        }
        return await service.post('/job/cv/userskill/', body);
    }

    static async deleteUserSkill(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/userskill/${id}`);
    }

    static async saveLanguage(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/userlanguage/${body.id}`, body);
        }
        return await service.post('/job/cv/userlanguage/', body);
    }

    static async deleteLanguage(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/userlanguage/${id}`);
    }

    static async saveCertificate(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/certificate/${body.id}`, body);
        }
        return await service.post('/job/cv/certificate/', body);
    }

    static async deleteCertificate(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/certificate/${id}`);
    }

    static async saveReference(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/reference/${body.id}`, body);
        }
        return await service.post('/job/cv/reference/', body);
    }

    static async deleteReference(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/reference/${id}`);
    }

    static async saveAward(body) {
        const service = new HttpService();
        if (body.id) {
            return await service.put(`/job/cv/award/${body.id}`, body);
        }
        return await service.post('/job/cv/award/', body);
    }

    static async deleteAward(id) {
        const service = new HttpService();
        return await service.delete(`/job/cv/award/${id}`);
    }

    // Fetch all user profile data for AI CV generation
    static async getUserProfileDataForAI() {
        const service = new HttpService();
        const MAX_ITEMS = 3;
        
        try {
            // Fetch all user-level items (without cv parameter)
            const [experiences, education, skills, languages, certificates, awards] = await Promise.all([
                service.get('/job/cv/jobexperience/', null).catch(() => ({ results: [] })),
                service.get('/job/cv/education/', null).catch(() => ({ results: [] })),
                service.get('/job/cv/userskill/', null).catch(() => ({ results: [] })),
                service.get('/job/cv/userlanguage/', null).catch(() => ({ results: [] })),
                service.get('/job/cv/certificate/', null).catch(() => ({ results: [] })),
                service.get('/job/cv/award/', null).catch(() => ({ results: [] }))
            ]);

            // Limit each category to MAX_ITEMS
            const limitArray = (arr) => Array.isArray(arr) ? arr.slice(0, MAX_ITEMS) : [];

            return {
                experiences: limitArray(Array.isArray(experiences) ? experiences : (experiences?.results || [])),
                education: limitArray(Array.isArray(education) ? education : (education?.results || [])),
                skills: limitArray(Array.isArray(skills) ? skills : (skills?.results || [])),
                languages: limitArray(Array.isArray(languages) ? languages : (languages?.results || [])),
                certificates: limitArray(Array.isArray(certificates) ? certificates : (certificates?.results || [])),
                awards: limitArray(Array.isArray(awards) ? awards : (awards?.results || []))
            };
        } catch (error) {
            console.error('Error fetching user profile data:', error);
            return {
                experiences: [],
                education: [],
                skills: [],
                languages: [],
                certificates: [],
                awards: []
            };
        }
    }

    // Format user profile data as text for AI
    static formatProfileDataForAI(profile, profileData) {
        const formatDate = (dateString) => {
            if (!dateString) return '';
            try {
                const date = new Date(dateString);
                return date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                });
            } catch {
                return dateString;
            }
        };

        const sections = [];

        // Profile Information
        if (profile) {
            const profileInfo = [];
            if (profile.first_name || profile.last_name) {
                profileInfo.push(`Name: ${profile.first_name || ''} ${profile.last_name || ''}`.trim());
            }
            if (profile.email) {
                profileInfo.push(`Email: ${profile.email}`);
            }
            if (profile.phone_number) {
                profileInfo.push(`Phone: ${profile.phone_number}`);
            }
            if (profile.city) {
                const location = profile.city + (profile.country?.name ? `, ${profile.country.name}` : '');
                profileInfo.push(`Location: ${location}`);
            }
            if (profile.home_address) {
                profileInfo.push(`Address: ${profile.home_address}`);
            }
            if (profile.about) {
                profileInfo.push(`About: ${profile.about}`);
            }
            if (profile.website) {
                profileInfo.push(`Website: ${profile.website}`);
            }
            if (profile.linkedin) {
                profileInfo.push(`LinkedIn: ${profile.linkedin}`);
            }
            
            if (profileInfo.length > 0) {
                sections.push('PROFILE INFORMATION');
                sections.push(profileInfo.join('\n'));
            }
        }

        // Work Experience
        if (profileData.experiences && profileData.experiences.length > 0) {
            sections.push('\nWORK EXPERIENCE');
            profileData.experiences.forEach((exp) => {
                const expLines = [];
                if (exp.title) expLines.push(`Position: ${exp.title}`);
                if (exp.company_title) expLines.push(`Company: ${exp.company_title}`);
                if (exp.start_date) {
                    const startDate = formatDate(exp.start_date);
                    const endDate = exp.end_date ? formatDate(exp.end_date) : 'Present';
                    expLines.push(`Period: ${startDate} - ${endDate}`);
                }
                if (exp.description) expLines.push(`Description: ${exp.description}`);
                if (expLines.length > 0) {
                    sections.push(expLines.join('\n'));
                    sections.push('---');
                }
            });
        }

        // Education
        if (profileData.education && profileData.education.length > 0) {
            sections.push('\nEDUCATION');
            profileData.education.forEach((edu) => {
                const eduLines = [];
                if (edu.level) eduLines.push(`Level: ${edu.level}`);
                if (edu.field_of_study) eduLines.push(`Field: ${edu.field_of_study}`);
                if (edu.institution) eduLines.push(`Institution: ${edu.institution}`);
                if (edu.start_date) {
                    const startDate = formatDate(edu.start_date);
                    const endDate = edu.end_date ? formatDate(edu.end_date) : 'Present';
                    eduLines.push(`Period: ${startDate} - ${endDate}`);
                }
                if (edu.description) eduLines.push(`Description: ${edu.description}`);
                if (eduLines.length > 0) {
                    sections.push(eduLines.join('\n'));
                    sections.push('---');
                }
            });
        }

        // Skills
        if (profileData.skills && profileData.skills.length > 0) {
            sections.push('\nSKILLS');
            profileData.skills.forEach((skill) => {
                const skillLines = [];
                if (skill.title) skillLines.push(`• ${skill.title}`);
                if (skill.years_of_experience) skillLines.push(`  Experience: ${skill.years_of_experience} years`);
                if (skillLines.length > 0) {
                    sections.push(skillLines.join('\n'));
                }
            });
        }

        // Languages
        if (profileData.languages && profileData.languages.length > 0) {
            sections.push('\nLANGUAGES');
            profileData.languages.forEach((lang) => {
                const langLines = [];
                if (lang.language?.name) langLines.push(`• ${lang.language.name}`);
                if (lang.proficiency_level) langLines.push(`  Proficiency: ${lang.proficiency_level}`);
                if (langLines.length > 0) {
                    sections.push(langLines.join('\n'));
                }
            });
        }

        // Certificates
        if (profileData.certificates && profileData.certificates.length > 0) {
            sections.push('\nCERTIFICATES & LICENSES');
            profileData.certificates.forEach((cert) => {
                const certLines = [];
                if (cert.title) certLines.push(`Certificate: ${cert.title}`);
                if (cert.description) certLines.push(`Description: ${cert.description}`);
                if (cert.expiration_month && cert.expiration_year) {
                    certLines.push(`Expiration: ${cert.expiration_month}/${cert.expiration_year}`);
                }
                if (certLines.length > 0) {
                    sections.push(certLines.join('\n'));
                    sections.push('---');
                }
            });
        }

        // Awards
        if (profileData.awards && profileData.awards.length > 0) {
            sections.push('\nAWARDS & ACHIEVEMENTS');
            profileData.awards.forEach((award) => {
                const awardLines = [];
                if (award.title) awardLines.push(`Award: ${award.title}`);
                if (award.awarded_date) awardLines.push(`Date: ${formatDate(award.awarded_date)}`);
                if (award.description) awardLines.push(`Description: ${award.description}`);
                if (awardLines.length > 0) {
                    sections.push(awardLines.join('\n'));
                    sections.push('---');
                }
            });
        }

        return sections.join('\n');
    }

    // Generate CV with AI
    static async generateCVWithAI(data) {
        console.log('Generating CV with AI:', data);
        const service = new HttpService();
        return await service.post('/job/cv/ai/generate/', data);
    }
}

